import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { DateRange } from '../../types/dashboard';

interface DateFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  variant?: 'light' | 'dark';
}

type PeriodOption = 'current' | 'last' | 'last3' | 'custom';

const PERIOD_OPTIONS: { value: PeriodOption; label: string }[] = [
  { value: 'current', label: 'Mois en cours' },
  { value: 'last',    label: 'Mois dernier' },
  { value: 'last3',   label: '3 derniers mois' },
];

// Format a local Date as 'YYYY-MM-DD' without UTC conversion
const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Parse a 'YYYY-MM-DD' string as a local Date (avoids UTC-shift of new Date(str))
const parseDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export default function DateFilter({ value, onChange, variant = 'light' }: DateFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>('last3');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set default range on mount
  useEffect(() => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    onChange({
      startDate: toDateStr(startDate),
      endDate: toDateStr(endDate)
    });
  }, []);

  const handlePeriodSelect = (period: PeriodOption) => {
    setSelectedPeriod(period);
    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (period) {
      case 'current':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last3':
        startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      default:
        return;
    }

    onChange({
      startDate: toDateStr(startDate),
      endDate: toDateStr(endDate)
    });
    setIsOpen(false);
  };

  const formatDateRange = () => {
    const start = parseDate(value.startDate);
    const end   = parseDate(value.endDate);
    const today = new Date();

    if (
      start.getFullYear() === today.getFullYear() &&
      start.getMonth() === today.getMonth() &&
      end.getDate() === new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
    ) {
      return `Mois en cours`;
    }

    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })} – ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={variant === 'dark'
          ? 'flex items-center gap-2 px-3 py-2 text-sm border border-white/15 rounded-xl text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors cursor-pointer'
          : 'flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40 bg-white text-slate-700 hover:border-slate-300'
        }
      >
        <Calendar size={14} className={variant === 'dark' ? 'text-white/60 shrink-0' : 'text-slate-400 shrink-0'} />
        <span>{formatDateRange()}</span>
        <ChevronDown size={14} className={`${variant === 'dark' ? 'text-white/60' : 'text-slate-400'} shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="fixed z-50 w-64 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
          style={{
            top: ref.current ? ref.current.getBoundingClientRect().bottom + window.scrollY + 4 : 0,
            left: ref.current ? ref.current.getBoundingClientRect().left + window.scrollX : 0,
          }}
        >
          {/* Quick picks */}
          <div className="p-1.5">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${selectedPeriod === opt.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}
                onClick={() => handlePeriodSelect(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="border-t border-slate-100" />

          {/* Custom range */}
          <div className="p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Période personnalisée</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Début</label>
                <input
                  type="date"
                  value={value.startDate}
                  onChange={(e) => {
                    setSelectedPeriod('custom');
                    onChange({ ...value, startDate: e.target.value });
                  }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Fin</label>
                <input
                  type="date"
                  value={value.endDate}
                  onChange={(e) => {
                    setSelectedPeriod('custom');
                    onChange({ ...value, endDate: e.target.value });
                  }}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
